import { UserRole } from '../types';

export interface StoredUser {
  id: string; // ID del usuario (uid de Firebase)
  email: string;
  currentRole: string;
  permissions: Array<{
    email: string;
    roleName: string;
    status: string;
    createdAt: string;
    updatedAt: string;
  }>;
}

// Roles disponibles con sus permisos
const ROLES: Record<string, UserRole> = {
  owner: {
    id: 'owner',
    name: 'Propietario',
    permissions: ['read', 'write', 'delete', 'admin', 'sales_reports', 'manage_users', 'manage_restaurants']
  },
  manager: {
    id: 'manager',
    name: 'Gerente',
    permissions: ['read', 'write', 'delete', 'admin', 'sales_reports', 'manage_users']
  },
  kitchen: {
    id: 'kitchen',
    name: 'Cocina',
    permissions: ['read', 'write', 'kitchen_orders']
  },
  waiter: {
    id: 'waiter',
    name: 'Mesero',
    permissions: ['read', 'write', 'waiter_orders']
  },
  comensal: {
    id: 'comensal',
    name: 'Cliente',
    permissions: ['read']
  }
};

class UserStorageService {
  private readonly USER_KEY = 'ots_user_data';
  private cachedUser: StoredUser | null = null;

  /**
   * Guarda el usuario en localStorage
   */
  async saveUser(userData: any): Promise<void> {
    try {
      const storedUser: StoredUser = {
        id: userData.id || userData.uid || '', // Usar id o uid de Firebase
        email: userData.email,
        currentRole: userData.currentRole || 'comensal',
        permissions: userData.permissions || []
      };

      localStorage.setItem(this.USER_KEY, JSON.stringify(storedUser));
      this.cachedUser = storedUser;

    } catch (error) {
      console.error('❌ Error guardando usuario en storage:', error);
    }
  }

  /**
   * Obtiene el usuario del localStorage
   */
  async getUser(): Promise<StoredUser | null> {
    try {
      if (this.cachedUser) {
        return this.cachedUser;
      }

      const userData = localStorage.getItem(this.USER_KEY);
      if (!userData) {
        return null;
      }

      const storedUser = JSON.parse(userData) as StoredUser;
      this.cachedUser = storedUser;
      return storedUser;
    } catch (error) {
      console.error('❌ Error obteniendo usuario del storage:', error);
      return null;
    }
  }

  /**
   * Limpia el usuario del localStorage
   */
  async clearUser(): Promise<void> {
    try {
      localStorage.removeItem(this.USER_KEY);
      this.cachedUser = null;
    } catch (error) {
      console.error('❌ Error limpiando usuario del storage:', error);
    }
  }

  /**
   * Calcula el rol más alto del usuario basado en sus permisos
   */
  getUserRole(): UserRole | null {
    try {
      const user = this.cachedUser;
      
      
      if (!user) {
        
        return ROLES.comensal;
      }

      // Jerarquía de roles (números más bajos = roles más altos)
      const roleHierarchy: Record<string, number> = {
        'owner': 1,
        'manager': 2,
        'kitchen': 3,
        'waiter': 4,
        'comensal': 5
      };
      
      let highestRole = 'comensal';
      let lowestLevel = 5;
      
      // Verificar el currentRole primero
      if (user.currentRole && roleHierarchy[user.currentRole] < lowestLevel) {
        highestRole = user.currentRole;
        lowestLevel = roleHierarchy[user.currentRole];
        
      }
      
      // Verificar todos los permisos del usuario
      if (user.permissions && user.permissions.length > 0) {
        
        
        user.permissions.forEach((permission: any) => {
          const permissionRole = permission.roleName;
          if (roleHierarchy[permissionRole] < lowestLevel) {
            highestRole = permissionRole;
            lowestLevel = roleHierarchy[permissionRole];
            
          }
        });
      }
      
      
      return ROLES[highestRole] || ROLES.comensal;
    } catch (error) {
      console.error('❌ Error calculando rol del usuario:', error);
      return ROLES.comensal;
    }
  }

  /**
   * Verifica si el usuario tiene un permiso específico
   */
  hasPermission(permission: string): boolean {
    try {
      const user = this.cachedUser;
      if (!user || !user.permissions) {
        return false;
      }

      // Obtener el rol más alto del usuario
      const userRole = this.getUserRole();
      if (!userRole) {
        return false;
      }

      // Verificar si el rol del usuario tiene el permiso
      return userRole.permissions.includes(permission);
    } catch (error) {
      console.error('❌ Error verificando permiso:', error);
      return false;
    }
  }

  /**
   * Verifica si el usuario es admin
   */
  isAdmin(): boolean {
    return this.hasPermission('admin');
  }

  /**
   * Verifica si el usuario puede ver reportes de ventas
   */
  canViewSalesReports(): boolean {
    return this.hasPermission('sales_reports');
  }

  /**
   * Verifica si el usuario es propietario
   */
  isOwner(): boolean {
    const role = this.getUserRole();
    return role?.id === 'owner';
  }

  /**
   * Verifica si el usuario es gerente
   */
  isManager(): boolean {
    const role = this.getUserRole();
    return role?.id === 'manager';
  }

  /**
   * Verifica si el usuario es gerente o cocinero
   */
  isManagerOrKitchen(): boolean {
    const role = this.getUserRole();
    return role?.id === 'manager' || role?.id === 'kitchen';
  }

  /**
   * Verifica si el usuario es cocinero
   */
  isKitchen(): boolean {
    const role = this.getUserRole();
    return role?.id === 'kitchen';
  }

  /**
   * Verifica si el usuario es mesero
   */
  isWaiter(): boolean {
    const role = this.getUserRole();
    return role?.id === 'waiter';
  }

  /**
   * Obtiene todos los permisos del usuario
   */
  getAllPermissions(): string[] {
    try {
      const userRole = this.getUserRole();
      return userRole ? userRole.permissions : [];
    } catch (error) {
      console.error('❌ Error obteniendo permisos:', error);
      return [];
    }
  }

  /**
   * Obtiene información detallada del usuario
   */
  getUserInfo() {
    const user = this.cachedUser;
    const role = this.getUserRole();
    
    return {
      email: user?.email,
      currentRole: user?.currentRole,
      highestRole: role?.id,
      roleName: role?.name,
      permissions: this.getAllPermissions(),
      permissionsCount: user?.permissions.length || 0
    };
  }
}

// Exportar una instancia única del servicio
export const userStorage = new UserStorageService(); 